import { useCallback } from 'react';
import first from 'lodash/first';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters';
import {
	isOptimisticIssue,
	type OptimisticIssuePayload,
} from '@atlassian/jira-business-issue-create';
import { useCacheActions } from '@atlassian/jira-business-list-handlers';
import { ff } from '@atlassian/jira-feature-flagging';
import { TIMELINE_PAGE_SIZE_ARGS, fieldIds } from '../../common/constants';
import { getVariables } from '../../common/utils/get-variables';
import { useIssueParentMap } from '../../common/utils/use-issue-parent-map';
import { createOptimisticIssue } from '../../services/create-issue';
import type {
	JWMTimelineViewItems,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMTimelineViewItemsNode,
} from '../../services/get-timeline-view-issues/__generated_apollo__/JWMTimelineViewItems';
import { TIMELINE_VIEW_ITEMS_QUERY } from '../../services/get-timeline-view-issues/gql';
import { addChildToParentTimeline } from '../../services/get-timeline-view-issues/utils';

export const useOptimisticIssueCreate = () => {
	const { quickFiltersJql, hierarchyParent, jqlContext, isHidingDoneItems } =
		useFilteredViewContextInput({
			view: 'timeline',
		});

	const { hierarchyLevels } = useIssueTypesAndFields({ issueOperation: 'VIEW' });
	const hierarchyLevel = first(hierarchyLevels)?.level;

	const [{ issueParentMap }] = useIssueParentMap();
	const { updateItemsInCache, addToCache, removeFromCache } = useCacheActions();

	const getUpdateVariables = useCallback(
		(parentId: string) => {
			const grandparentIssueId = issueParentMap.get(parentId);
			const baseVariables = {
				jqlContext,
				fieldIds,
				quickFiltersJql,
				isHidingDoneItems,
			};

			if (grandparentIssueId && hierarchyParent) {
				return getVariables({
					...baseVariables,
					hierarchyParent: undefined,
					parentIssue: {
						hierarchyType: hierarchyParent,
						issueId: grandparentIssueId,
					},
				});
			}

			return getVariables({
				...baseVariables,
				hierarchyLevel,
				hierarchyParent,
			});
		},
		[
			hierarchyLevel,
			hierarchyParent,
			isHidingDoneItems,
			issueParentMap,
			jqlContext,
			quickFiltersJql,
		],
	);

	// Update the cache with the newly created issue, replacing any optimistic issue if present
	const updateIssue = useCallback(
		(item: JWMTimelineViewItemsNode, parentId?: string) => {
			// handle optimistic update for children item if the hierarchy feature is enabled
			if (parentId != null && hierarchyParent != null) {
				// update the parent issue to contain the new subtask so it will be shown in the UI correctly
				if (ff('jwm.visual-hierarchy-milestone-1')) {
					if (isOptimisticIssue(item.issueId)) {
						updateItemsInCache<JWMTimelineViewItemsNode, JWMTimelineViewItems>({
							query: TIMELINE_VIEW_ITEMS_QUERY,
							variables: getUpdateVariables(parentId),
							issueIds: [parentId],
							updateItem: addChildToParentTimeline,
						});
					}
				} else {
					updateItemsInCache<JWMTimelineViewItemsNode, JWMTimelineViewItems>({
						query: TIMELINE_VIEW_ITEMS_QUERY,
						variables: getVariables({
							jqlContext,
							fieldIds,
							quickFiltersJql,
							isHidingDoneItems,
							hierarchyParent,
						}),
						issueIds: [parentId],
						updateItem: addChildToParentTimeline,
					});
				}

				// update the children cache to add the new subtask
				addToCache<JWMTimelineViewItemsNode, JWMTimelineViewItems>({
					newNodes: [item],
					query: TIMELINE_VIEW_ITEMS_QUERY,
					variables: getVariables({
						jqlContext,
						fieldIds,
						quickFiltersJql,
						isHidingDoneItems,
						hierarchyParent: undefined,
						parentIssue: {
							hierarchyType: hierarchyParent,
							issueId: parentId,
						},
					}),
				});

				return;
			}

			addToCache({
				newNodes: [item],
				query: TIMELINE_VIEW_ITEMS_QUERY,
				variables: {
					...getVariables({
						jqlContext,
						fieldIds,
						quickFiltersJql,
						isHidingDoneItems,
						hierarchyParent,
						...(ff('jwm.visual-hierarchy-milestone-1') && { hierarchyLevel }),
					}),
					...TIMELINE_PAGE_SIZE_ARGS,
				},
			});
		},
		[
			hierarchyParent,
			addToCache,
			jqlContext,
			quickFiltersJql,
			isHidingDoneItems,
			hierarchyLevel,
			updateItemsInCache,
			getUpdateVariables,
		],
	);

	// Add an optimistic issue to the cache
	const addOptimisticIssue = useCallback(
		(issue: OptimisticIssuePayload, parentId?: string) => {
			const optimisticItem = createOptimisticIssue(issue);
			updateIssue(optimisticItem, parentId);
		},
		[updateIssue],
	);

	const removeOptimisticIssue = useCallback(
		(issueId: number, parentId?: string) => {
			let variables;

			if (parentId != null && hierarchyParent != null) {
				variables = getVariables({
					jqlContext,
					fieldIds,
					quickFiltersJql,
					isHidingDoneItems,
					hierarchyParent: undefined,
					parentIssue: {
						hierarchyType: hierarchyParent,
						issueId: parentId,
					},
				});
			} else {
				variables = getVariables({
					jqlContext,
					fieldIds,
					quickFiltersJql,
					isHidingDoneItems,
					hierarchyParent,
					...(ff('jwm.visual-hierarchy-milestone-1') && { hierarchyLevel }),
				});
			}

			removeFromCache({ query: TIMELINE_VIEW_ITEMS_QUERY, variables, issueId });
		},
		[
			hierarchyParent,
			removeFromCache,
			jqlContext,
			quickFiltersJql,
			isHidingDoneItems,
			hierarchyLevel,
		],
	);

	return { addOptimisticIssue, updateIssue, removeOptimisticIssue };
};
